import { modalDefaults } from './Modal_defaults';
import MegaModal from './MegaModal';
import Table from '@dataplan/mega-tables/src/Table.lib';
import Tabs from './Tabs';
import { Form, Inputs } from '@dataplan/mega-forms/src/Form.lib';
import HTTP from './HTTP';
import { forOwn, unset, extend, find } from 'lodash-es';

export default class Modal extends MegaModal {

	/**
	 * Constructs a modal based on the options given (default being a basic modal)
	 * @param options ({})
	 */
	constructor (options) {
		super();

		this.collapsibleTables = [];

		this.options = {
			errors: [],
			type: false,
			title: '',
			data: '',
			headData: false,
			tabs: [],
			form: {},
			tableData: {},
			headerCloseButton: true,
			confirmation: false,
			width: 800,
			height: null,
			footer: {},
			ajax: false,
			ajaxHandlers: {
				success: (response, element) => {
					let content = JSON.parse(response);
					element.bodyContent.insertAdjacentHTML('beforeend', content.data);
				},
				failure: (data, element) => {
					element.bodyContent.insertAdjacentHTML('beforeend', 'Failed to retrieve data');
				},
			},
			closeButtonSvg: '#cancel-ico',
			closeEvent: null,
		};

		extend(this.options, options);
		modalDefaults.id++;
		this.id = modalDefaults.id;

		this.overLay();
		this.modalBody();
		this.lockBody();

		if (this.options.type) {
			this[this.options.type]();
		} else {
			this.generic();
		}

		modalDefaults.modals[modalDefaults.id] = this;
	}

	/**
	 * Render the modal
	 */
	render () {
		this.appendModal(this.modal);

		this.initializeEvents();
		this.resizeElements();
	}

	/**
	 * Generates the modal body
	 */
	modalBody () {
		this.modal = document.createElement('div');
		this.modal.classList.add('mega-modal');
		this.modal.classList.add('modal-window');
		this.modal.style.display = 'block';
		this.modal.setAttribute('mega-modal-id', modalDefaults.id);
		this.modal.style.width = `${this.options.width}px`;

		this.modalHeader = this.createHeader();

		if (this.options.headerCloseButton) {
			this.actionContainer = document.createElement('div');
			this.actionContainer.classList.add('action-container');

			this.modalcloseAnchor = document.createElement('a');
			this.modalcloseAnchor.href = '#';
			this.modalcloseAnchor.title = 'Close window';
			this.modalcloseAnchor.classList.add('close-anchor');
			this.closeSVG = this.generateSVG(this.options.closeButtonSvg || '');
			this.modalcloseAnchor.appendChild(this.closeSVG);
			this.actionContainer.appendChild(this.modalcloseAnchor);
			this.modalHeader.appendChild(this.actionContainer);
		}

		this.bodyContent = document.createElement('div');
		this.bodyContent.classList.add('modal-content');

		if (this.options.height) {
			this.bodyContent.style.height = `${this.options.height}px`;
		}

		this.modal.appendChild(this.modalHeader);
		this.modal.appendChild(this.bodyContent);
	}

	/**
	 * Creates header node for the modal
	 * @return {HTMLElement} The header node
	 */
	createHeader () {
		let header = document.createElement('div');
		header.classList.add('modal-header');

		this.headerTitle = document.createElement('h2');
		this.headerTitle.appendChild(document.createTextNode(this.options.title));
		header.appendChild(this.headerTitle);

		return header;
	}

	/**
	 * Default modal is the modal which will open without a 'type' option added.
	 * Content will handle a node element and strings
	 *
	 * optional: title, filterTarget, confirmation
	 */
	generic () {
		let usesAjax = this.options.ajax;

		if (!usesAjax && typeof this.options.data === 'object') {
			this.bodyContent.appendChild(this.options.data);
		} else if (!usesAjax) {
			this.bodyContent.innerHTML = this.options.data;
		}

		if (usesAjax) {
			let ajaxObj = {
				url: this.options.ajax.url,
				timeout: 5000,
				onSuccess: (responseData) => {
					this.options.ajaxHandlers.success(
						responseData,
						this
					);
				},
				onFailure: (status, callbackData, responseText) => {
					this.options.ajaxHandlers.failure(
						status,
						callbackData,
						responseText
					);
				},
			};

			let request = new HTTP();
			request.get(ajaxObj);
		}
	}

	/**
	 *
	 * @param table
	 */
	collapsibleTableHandler (table) {
		this.collapsibleTables.push(table.collapsibleTable);
	}

	/**
	 * Generates a table and embeds it into the modal
	 * @param options
	 */
	table (options) {
		let tableObj = options || this.options.tableData;
		let table = new Table(tableObj);

		if (table.filterTarget) {
			this.bodyContent.appendChild(table.tableFilter);
		}

		table.table.classList.add('mega-modal-table');

		if (table.autoAppend) {
			this.bodyContent.appendChild(table.getTable());
		}

		if (table.collapsibleTable !== undefined) {
			this.collapsibleTableHandler(table);
		}

		return table;
	}

	/**
	 * Generates tabs so a user can switch between multiple conditions within a modal.
	 * This allows you to assign multiple forms, tables etc within a single modal and simply tab between
	 * each window
	 */
	tab () {
		let tabObj = this.options.tabs;
		this.modalTabs = new Tabs(tabObj, this);
		this.modal.insertBefore(this.modalTabs.tabContainer, this.bodyContent);
	}

	/**
	 * Generates a form and embeds it into the modal
	 * @param options
	 */
	form (options) {
		this.formObj = new Form((options || this.options.form), this);
		this.formObj.form.classList.add('mega-modal-form');
		this.bodyContent.appendChild(this.formObj.form);
	}

	/**
	 * Generates customisable footer actions to be added to the footer of the modal
	 * @param options
	 */
	footerActions (options) {
		extend(this.options.footer, options);

		this.actionfooter = document.createElement('div');
		this.actionfooter.classList.add('modal-footer');

		this.actionfooterDetails = document.createElement('div');
		this.actionfooterDetails.classList.add('footer-details');

		this.footerActionsContainer = document.createElement('div');
		this.footerActionsContainer.classList.add('footer-actions');

		let actions = this.options.footer.actions;
		this.footerActionInputs = {};

		forOwn(actions, (action, key) => {
			action.type = action.type || 'button';

			let footerAction = new Inputs(action, this);
			this.footerActionInputs[key] = footerAction.input;
			this.footerActionsContainer.appendChild(footerAction.input);
		});

		this.actionfooter.appendChild(this.actionfooterDetails);
		this.actionfooter.appendChild(this.footerActionsContainer);
		this.modal.appendChild(this.actionfooter);
	}

	/**
	 * Appends the modal to the overlay
	 * @param modal
	 */
	appendModal (modal) {
		this.overlay.appendChild(modal);
		document.body.classList.add('mega-modal-open');
	}

	/**
	 * Adds related event listeners to the modal. (close, resizing, DOM observer)
	 */
	initializeEvents () {
		if (this.options.headerCloseButton) {
			this.closeSVG.addEventListener('click', this.close.bind(this));
		}

		this.resizeModals = this.resizeElements.bind(this);
		this.contentObserver();

		window.addEventListener('resize', this.resizeModals);
	}

	/**
	 * Close the modal
	 * @param event
	 */
	close (event) {
		if (event) {
			event.preventDefault();
		}

		modalDefaults.id--;
		this.lockBody();

		let previous = this.modal.previousElementSibling
			? this.modal.previousElementSibling.getAttribute('mega-modal-id')
			: false;

		this.modal.parentNode.removeChild(this.modal);

		if (previous) {
			this.resizeElements();
			this.resizePrevious(previous);
		} else {
			document.body.classList.remove('mega-modal-open');
		}

		modalDefaults.modalPosition = modalDefaults.modalPosition.filter((x) => x.id !== this.id);

		if (typeof this.options.closeEvent === 'function') {
			this.options.closeEvent();
		}

		window.removeEventListener('resize', this.resizeModals);
	}

	/**
	 * Observes the DOM for any changes within the DOM and makes the necessary adjustments to the size if required
	 */
	contentObserver () {
		let modalTarget = this.modal;
		let selector = 'current-modal';
		let config = {childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class']};

		let observer = new MutationObserver(() => {
			observer.disconnect();

			if (modalTarget.classList.contains(selector)) {
				this.resizeContainer();
				this.resizeModals();
			}

			observer.observe(modalTarget, config);
		});

		observer.observe(modalTarget, config);
	}

	/**
	 * Removes the modal body content
	 * @param excludeNodes An array of nodes to not remove
	 */
	removeBodyContent (e, excludeNodes) {
		let bodyContent = this.bodyContent,
			footerActions = this.actionfooter;

		for (let i = bodyContent.childNodes.length; i > 0; i--){
			let childNode = bodyContent.childNodes[i - 1];

			let childIsExcluded = (excludeNodes !== undefined
				&& excludeNodes.includes(childNode));

			if (childIsExcluded) {
				return;
			}

			bodyContent.removeChild(childNode);
		}

		if (footerActions) {
			footerActions.parentNode.removeChild(footerActions);
			unset(this, 'actionfooter');
		}
	}

	/**
	 * Adds a new key value property to the modal
	 * @param key
	 * @param value
	 */
	addProperty (key, value) {
		if (this[key] !== undefined) {
			this[key] = value;
		}
	}

	/**
	 * Helper function to re-trigger a selected tab
	 *
	 * Trigger the event of a given tab by its tab-name
	 * @param selector The target node
	 */
	launchTab (selector) {
		this.removeBodyContent();
		let tab = find(this.modalTabs.tabObjs, { action: selector });
		this.modalTabs.triggerTabEvent(tab);
		this.modalTabs.tabActive(tab);
	}

}
